import React from 'react'
import styled from 'styled-components'
import {
  COLOR_BRAND_ACCENT,
  COLOR_BRAND_PRIMARY,
  COLOR_SEMANTIC_ERROR_LIGHT,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_STRONG,
  COLOR_UI_BACKGROUND,
  COLOR_UI_BORDER,
  COLOR_UI_SURFACE,
  FONT_BASE,
  RADIUS_MD,
  SPACING_MD,
  SPACING_SM,
} from '@src/styles/generated-theme-values'
import { BuzzCommunityProjectionState } from '@modules/messaging/hooks/use-buzz-community-projection'
import { BuzzTeamMember } from '@modules/messaging/types'

const Projection = styled.section`
  margin-bottom: ${SPACING_MD};
  padding: ${SPACING_MD};
  border: 1px solid ${COLOR_UI_BORDER};
  border-radius: ${RADIUS_MD};
  background: ${COLOR_UI_SURFACE};
  color: ${COLOR_TEXT_PRIMARY};
  font-family: ${FONT_BASE};
`

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${SPACING_MD};
  margin-bottom: ${SPACING_MD};

  @media (max-width: 720px) {
    flex-direction: column;
  }
`

const Title = styled.h2`
  margin: 0 0 4px;
  color: ${COLOR_TEXT_STRONG};
  font-size: 18px;
`

const Description = styled.p`
  margin: 0;
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 13px;
  line-height: 1.45;
`

const Status = styled.span<{ $ready: boolean }>`
  flex: 0 0 auto;
  color: ${({ $ready }) => $ready ? COLOR_BRAND_PRIMARY : COLOR_BRAND_ACCENT};
  font-size: 12px;
`

const Groups = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACING_MD};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const Group = styled.div`
  min-width: 0;
`

const GroupTitle = styled.h3`
  margin: 0 0 ${SPACING_SM};
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 12px;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`

const IdentityList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_SM};
  margin: 0;
  padding: 0;
  list-style: none;
`

const Identity = styled.li`
  min-width: 0;
  padding: 10px;
  border: 1px solid ${COLOR_UI_BORDER};
  border-radius: ${RADIUS_MD};
  background: ${COLOR_UI_BACKGROUND};
`

const IdentityHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${SPACING_SM};
  color: ${COLOR_TEXT_STRONG};
  font-size: 14px;
`

const Badge = styled.span`
  color: ${COLOR_BRAND_ACCENT};
  font-size: 10px;
  text-transform: uppercase;
`

const IdentityId = styled.div`
  margin-top: 5px;
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 11px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PublicKey = styled.code`
  display: block;
  margin-top: 3px;
  color: ${COLOR_TEXT_SECONDARY};
  font-size: 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ErrorText = styled.div`
  color: ${COLOR_SEMANTIC_ERROR_LIGHT};
  font-size: 13px;
`

const RetryButton = styled.button`
  margin-top: ${SPACING_SM};
  padding: 6px 10px;
  border: 1px solid ${COLOR_BRAND_PRIMARY};
  border-radius: ${RADIUS_MD};
  background: transparent;
  color: ${COLOR_TEXT_STRONG};
  cursor: pointer;
`

function IdentityGroup({ label, members }: { label: string; members: BuzzTeamMember[] }) {
  return (
    <Group>
      <GroupTitle>{label} ({members.length})</GroupTitle>
      <IdentityList aria-label={`${label} in Equa Chat`}>
        {members.map((member) => (
          <Identity key={`${member.subjectType}:${member.subjectId}`}>
            <IdentityHeader>
              <strong>{member.displayName}</strong>
              <Badge>{member.subjectType === 'agent' ? 'AI team member' : member.role}</Badge>
            </IdentityHeader>
            <IdentityId title={member.subjectId}>Equa ID: {member.subjectId}</IdentityId>
            <PublicKey title={member.publicKey}>Nostr: {member.publicKey}</PublicKey>
          </Identity>
        ))}
      </IdentityList>
    </Group>
  )
}

export const BuzzTeamProjection: React.FC<BuzzCommunityProjectionState> = ({
  session, loading, error, refresh,
}) => {
  if (loading) {
    return <Projection aria-live="polite">Loading Equa Chat team identities...</Projection>
  }

  if (error || !session) {
    return (
      <Projection>
        <ErrorText role="alert">
          {error || 'The Equa Chat team projection is not available yet.'}
        </ErrorText>
        <Description>Messaging initializes and synchronizes the organization community.</Description>
        <RetryButton type="button" onClick={() => { void refresh() }}>Retry team projection</RetryButton>
      </Projection>
    )
  }

  const humans = session.members.filter((member) => member.subjectType === 'human')
  const agents = session.members.filter((member) => member.subjectType === 'agent')
  const ready = session.community.status === 'ready' && session.connection.state === 'ready'

  return (
    <Projection aria-labelledby="equa-chat-team-title">
      <Header>
        <div>
          <Title id="equa-chat-team-title">Equa Chat team identities</Title>
          <Description>
            This read-only Nostr identity projection is shared with organization Messaging.
            Equa remains the membership authority.
          </Description>
        </div>
        <Status $ready={ready} role="status">
          {ready ? 'Relay connected' : `Community ${session.community.status}`}
        </Status>
      </Header>
      <Groups>
        <IdentityGroup label="People" members={humans} />
        <IdentityGroup label="Equanauts" members={agents} />
      </Groups>
    </Projection>
  )
}
